/**
 * Tag config. Adding a category = one entry here; the type system and the
 * Browse pill row both derive from this object so no other file needs to change.
 */
export const TAG_CATEGORIES = {
  type: {
    label: 'Type',
    values: ['main', 'side', 'sauce'],
  },
} as const;

export type TagCategory = keyof typeof TAG_CATEGORIES;
export type TagValue<C extends TagCategory> = (typeof TAG_CATEGORIES)[C]['values'][number];
export type Tags = { [C in TagCategory]?: TagValue<C> };

export interface FlatTag {
  category: TagCategory;
  value: string;
}

/** Flat list of every (category, value) pair declared in `TAG_CATEGORIES`, in declaration order. */
export const ALL_TAGS: FlatTag[] = (Object.keys(TAG_CATEGORIES) as TagCategory[]).flatMap((cat) =>
  TAG_CATEGORIES[cat].values.map((value) => ({ category: cat, value })),
);

/** Title-cased display label for a tag value ("main" → "Main"). */
export function tagLabel(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Recipe matches when, for every category with selected values, the recipe's value is in the set. */
export function matchesTagSelection(
  tags: Tags | undefined,
  selected: Partial<Record<TagCategory, Set<string>>>,
): boolean {
  for (const cat of Object.keys(selected) as TagCategory[]) {
    const set = selected[cat];
    if (!set?.size) continue;
    const value = tags?.[cat];
    if (!(value && set.has(value))) return false;
  }
  return true;
}
